import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "@/data/content";
import { usePublicSettings } from "@/contexts/SettingsContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const settings = usePublicSettings();

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo-icon.webp"
              alt=""
              width="40"
              height="40"
              data-testid="nav-logo-image"
              className="w-10 h-10 shrink-0 object-contain"
            />
            <span className="font-display font-bold text-lg text-[#0F172A] leading-tight">
              P &amp; A Cleaning
              <span className="block text-[10px] tracking-[0.18em] uppercase text-[#475569] font-medium">
                Sacramento
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? "text-[#1E3A8A] bg-[#1E3A8A]/5"
                      : "text-[#475569] hover:text-[#1E3A8A] hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={settings.phone_href}
              data-testid="nav-call-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-[#1E3A8A] border border-[#1E3A8A]/15 hover:bg-[#1E3A8A]/5 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call / Text
            </a>
            <Link
              to="/request-estimate"
              data-testid="nav-estimate-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1E3A8A] text-white hover:bg-[#1E40AF] transition-colors shadow-soft"
            >
              Request an Estimate
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 rounded-lg text-[#0F172A] hover:bg-slate-100"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-slate-100 bg-white"
          data-testid="nav-mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-base font-medium ${
                    isActive
                      ? "text-[#1E3A8A] bg-[#1E3A8A]/5"
                      : "text-[#0F172A] hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <a
                href={settings.phone_href}
                data-testid="nav-mobile-call-button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-[#1E3A8A] border border-[#1E3A8A]/20"
              >
                <Phone className="w-4 h-4" /> Call / Text
              </a>
              <Link
                to="/request-estimate"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-estimate-button"
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold bg-[#1E3A8A] text-white"
              >
                Request Estimate
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
